
import { defineComponent, ref } from "vue";
import { EventBus } from "@/eventBus";
import { mapActions, mapGetters, mapState } from "vuex";
import { RootGetters } from "@/store/getters";
import { ISlide } from "@/models/slide";
import { RootActions } from "@/store/actions";
import SlidePreview from "@/modules/slides/components/SlidePreview.vue";
import { Direction, navigationUtils } from "@/utils/navigation";
import { Dialog } from "@progress/kendo-vue-dialogs";
import { WorkflowState } from "@/models/workflow-state";
import { RootMutations } from "@/store/mutations";
import { useI18n } from "vue-i18n";
import IDialog from "@/components/dialog/IDialog.vue";
import { modalTypes } from "@/components/dialog/modalTypes";
import IButton from "@/components/buttons/IButton.vue";

export default defineComponent({
  name: "SlideList",
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  data() {
    return {
      isPinned: false,
      navigationDirection: Direction.Next,
      isAutoSkipMode: false,
      errorModal: false,
      modalTypes,
    };
  },
  computed: {
    ...mapGetters({
      slides: RootGetters.SLIDES,
      isDirectLink: RootGetters.GET_IS_DIRECT_LINK,
      currentSlide: RootGetters.GET_CURRENT_SLIDE,
      queryParams: RootGetters.GET_QUERY_PARAMS,
      routeParams: RootGetters.GET_ROUTE_PARAMS,
      isFirstRead: RootGetters.IS_FIRST_READ,
      activeCase: RootGetters.GET_ACTIVE_CASE,
    }),
    ...mapState(["activeSlideId"]),
    fullPageView(): boolean {
      return this.isDirectLink && this.currentSlide?.id === "";
    },
    isAllSlidesComplete(): boolean {
      return this.slides.every((slide: ISlide) => {
        return slide.workflowState === WorkflowState.COMPLETE;
      });
    },
  },
  methods: {
    ...mapActions({
      navigateToSlide: RootActions.NAVIGATE_TO_SLIDE,
      displayCaseReport: RootActions.DISPLAY_CASE_REPORT,
    }),
    isSlidePinned(): boolean {
      return !this.currentSlide.id || this.isPinned;
    },
    getSlideBySlideID(slideId: string): ISlide {
      return this.slides.find((slide: ISlide) => slide.slideId === slideId);
    },
    getSlideByLogicalName(slideId: string): ISlide {
      return this.slides.find((slide: ISlide) => slide.logicalName === slideId);
    },
    getSlideIndexById(slideId: string): number {
      return this.slides.findIndex((slide: ISlide) => {
        return slide.slideId === slideId;
      });
    },
    handleNavigateToSlideEvent(): void {
      // TODO: we have to use isAutoSkipMode to prevent opening displayCaseReport upon each pagedown
      if (this.isFirstRead && this.isAllSlidesComplete && this.isAutoSkipMode) {
        this.displayCaseReport();
      } else {
        this.navigateToTheFollowingSlide();
      }
    },
    navigateToTheFollowingSlide(): void {
      let nextSlideIndex = this.calculateFollowingSlideIndex();
      this.navigateToSlide({
        slide: this.slides[nextSlideIndex],
        alertIndex: 0,
      });
    },
    calculateFollowingSlideIndex(): number {
      const currentSlideIndex = this.getSlideIndexById(this.activeSlideId);
      let followingSlideIndex;
      if (this.isAutoSkipMode) {
        const shouldSkipFn: Function = (slide: ISlide): boolean => {
          return slide.workflowState === WorkflowState.COMPLETE;
        };
        followingSlideIndex = navigationUtils.getFollowingIndex(
          this.slides,
          currentSlideIndex,
          shouldSkipFn,
          this.navigationDirection
        );
      } else {
        followingSlideIndex = navigationUtils.calculateTargetIndex(
          this.slides,
          currentSlideIndex,
          this.navigationDirection
        );
      }
      return followingSlideIndex;
    },
    handleNavigateToSlide(payload: any) {
      this.navigationDirection = payload.navigationDirection;
      this.isAutoSkipMode = payload.isAutoSkipMode;
      this.handleNavigateToSlideEvent();
    },
    handleKeysEvent(e: { key: string }) {
      switch (e.key) {
        case "PageDown":
          this.handleNavigateToSlide({
            navigationDirection: Direction.Next,
            isAutoSkipMode: false,
          });
          break;
        case "PageUp":
          this.handleNavigateToSlide({
            navigationDirection: Direction.Previous,
            isAutoSkipMode: false,
          });
          break;
        default:
          break;
      }
    },
    navigateToSlideEvent(payload: any) {
      this.handleNavigateToSlide(payload);
    },
    slideLoaded(data: any) {
      this.$store.commit(RootMutations.UPDATE_CURRENT_SLIDE, { data });
      this.$store.commit(RootMutations.SET_ACTIVE_SLIDE_ID, { activeSlideId: data.slide_id });
    },
  },
  mounted() {
    EventBus.on("navigateToSlideEvent", this.navigateToSlideEvent);
    EventBus.on("slide-loaded", this.slideLoaded);
    window.addEventListener("keydown", this.handleKeysEvent, false);
  },
  unmounted() {
    EventBus.off("navigateToSlideEvent", this.navigateToSlideEvent);
    EventBus.off("slide-loaded", this.slideLoaded);
    window.removeEventListener("keydown", this.handleKeysEvent, false);
  },
  components: {
    SlidePreview,
    "k-dialog": Dialog,
    IDialog,
    IButton,
  },
  watch: {
    slides(val, oldVal) {
      if (val.length === oldVal.length) return;
      let slide = null;
      if (this.routeParams?.name === "cl") {
        try {
          const clData = JSON.parse(atob(this.routeParams.params.slug));
          if (!clData.slide) return;
          slide = this.getSlideBySlideID(clData.slide);
        } catch (e) {
          console.error("IBEX Error parsing routeParams", e);
          this.errorModal = true;
        }
      }
      if (this.queryParams.get("slide")) {
        if (!oldVal.length && val.length) {
          slide = this.getSlideByLogicalName(this.queryParams.get("slide"));
        }
      }
      if (slide) {
        this.navigateToSlide({
          slide,
          alertIndex: 0,
        });
      } else if (slide === null) {
        return;
      } else {
        this.errorModal = true;
      }
    },
  },
});
